/**
 * Niet in het origineel: kleine rekenstukjes voor de schermen van het inloggen en de pagina
 * Spelleiders. Los van de componenten, zodat ze apart te testen zijn.
 */
import { keurWachtwoord, WACHTWOORD_MIN_LENGTE, type WachtwoordContext } from '@ame/shared';

/** `goed`: voldaan; `open`: nog niet (of pas bij opslaan te zien); `fout`: hier zit het mis. */
export type RegelStatus = 'goed' | 'open' | 'fout';
export type Regel = { tekst: string; status: RegelStatus };

/** Wat `keurWachtwoord` over een te kort wachtwoord zegt: een leeg wachtwoord geeft alleen dat. */
const TE_KORT = keurWachtwoord('')[0];

/**
 * De regels voor een nieuw wachtwoord als lijst om af te vinken, uit `keurWachtwoord`. Vast
 * staan de lengte, de naam en het datalek (dat controleert alleen de server). Wat
 * `keurWachtwoord` verder vindt, zoals een voorspelbaar woord, komt er als fout bij.
 */
export function wachtwoordRegels(wachtwoord: string, context: WachtwoordContext): Regel[] {
  const leeg = wachtwoord.length === 0;
  const zonderContext = keurWachtwoord(wachtwoord);
  // Wat er alleen met de naam of het e-mailadres erbij afgekeurd wordt.
  const eigen = keurWachtwoord(wachtwoord, context).filter((f) => !zonderContext.includes(f));
  const overig = zonderContext.filter((f) => f !== TE_KORT);

  return [
    {
      tekst: `Minstens ${WACHTWOORD_MIN_LENGTE} tekens`,
      status: wachtwoord.length >= WACHTWOORD_MIN_LENGTE ? 'goed' : 'open',
    },
    {
      tekst: 'Niet je naam of je e-mailadres',
      status: leeg ? 'open' : eigen.length > 0 ? 'fout' : 'goed',
    },
    { tekst: 'Niet in een bekend datalek (controleren we bij opslaan)', status: 'open' },
    ...overig.map((tekst): Regel => ({ tekst, status: 'fout' })),
  ];
}

/** De letters in het rondje: "Maarten Joosten" wordt "MJ", "Sanne de Vries" "SV". */
export function initialen(naam: string): string {
  const woorden = naam.trim().split(/\s+/).filter(Boolean);
  const eerste = woorden[0]?.[0] ?? '';
  const laatste = woorden.length > 1 ? (woorden.at(-1)?.[0] ?? '') : '';
  return (eerste + laatste).toUpperCase() || '?';
}

/** "22 oktober", met het jaar erbij als het niet dit jaar was. */
export function datumKort(iso: string, nu = new Date()): string {
  const datum = new Date(iso);
  return datum.toLocaleDateString('nl-NL', {
    day: 'numeric',
    month: 'long',
    ...(datum.getFullYear() !== nu.getFullYear() && { year: 'numeric' }),
  });
}

/** "Link verloopt over 23 uur", "... minder dan een uur" of "Link verlopen". */
export function verlooptTekst(verlooptOp: string, nu = Date.now()): string {
  const over = new Date(verlooptOp).getTime() - nu;
  if (over <= 0) return 'Link verlopen';
  const uren = Math.floor(over / 3_600_000);
  if (uren < 1) return 'Link verloopt over minder dan een uur';
  return `Link verloopt over ${uren} uur`;
}

/** De tekst van een fout, om op het scherm te zetten. */
export function tekst(reden: unknown): string {
  return reden instanceof Error ? reden.message : String(reden);
}
